import React, { useEffect, useRef, useState } from 'react'
import {
  Spacing,
  Heading,
  Button,
  MobileFilterHeader,
  useSwipeClose,
  useIsMobile,
  useStickyFilters,
  BREAKPOINT_SMALL,
} from '@big-red-group/storefront-common-ui'
import {
  GridLeft,
  GridRight,
  FilterTriggerBar,
  Close,
  FilterDropdownWrap,
  FilterDropdownPanel,
  PanelContent,
  FilterDropdownButton,
  ListingWrap,
  NoResultsWrapper,
  FilterBg,
  FilterDropdownButtonLabel,
  FilterIndicator,
  ViewMore,
  ViewMoreWrapper,
  ClearButton,
  ResultsCountWrapper,
  StickyFiltersWrapper,
  ProductsHeadingWrapper,
} from '@/components/widgets/FiltersListingPreview.styles'
import {
  FilterDropdown,
  FiltersListingPreviewEntityType,
  PlpProduct,
  SearchFilterDecorator,
  WidgetCategoryLink,
  WidgetRegionLink,
} from '@/types'
import { FILTER_DROPDOWNS, PRICING_FILTERS } from '@/constant/plp'
import Icon from '../Icons'
import ListingFilters from '../ListingFilters'
import { sortProducts } from '@/helpers/SearchHelper'
import ListingPreview from './ListingPreview'
import { roundDecimalNumber } from '@/helpers/ClientSideRenderHelper'
import { filterHeading, renderCategoryFilters, renderPriceFilters, renderRegionFilters } from '@/helpers/FilterHelper'
import { Footer, FooterButtons } from '../ListingFilters.styles'
import Select from '../Select'
import { useRouter } from 'next/router'
import { BASE_MOBILE_TOP_BAR_HEIGHT, HAPPY_HOUR_BANNER_HEIGHT } from '@/constant/ui'
import { useDefaultProperties } from '@/hooks/DefaultPropertiesHook'
import { createPortal } from 'react-dom'

type FiltersListingPreviewProps = {
  entityType: FiltersListingPreviewEntityType
  products: PlpProduct[]
  regions: WidgetRegionLink[] | undefined
  categories: WidgetCategoryLink[] | undefined
  title?: string
  handlerKey?: string
  changeUrl?: boolean
  showTotal?: boolean
  alwaysShowAllFilterSideBar?: boolean
}

const sortOptions = [
  { name: 'popular', label: 'Sort: Popularity' },
  { name: 'priceLow', label: 'Price (Low to High)' },
  { name: 'priceHigh', label: 'Price (High to Low)' },
  { name: 'ratingHigh', label: 'Rating (High to Low)' },
  { name: 'alphabeticallyHigh', label: 'Alphabetically (A-Z)' },
  { name: 'alphabeticallyLow', label: 'Alphabetically (Z-A)' },
]

let pageSize = 15

const FilterListingPreview = ({
  entityType,
  title,
  products,
  handlerKey,
  regions,
  categories,
  changeUrl,
  showTotal,
  alwaysShowAllFilterSideBar,
}: FiltersListingPreviewProps) => {
  const defaultProperties = useDefaultProperties()
  const happyHour = !!defaultProperties?.happyHourEvent
  // Adjust MOBILE_TOP_BAR_HEIGHT based on happyHour banner
  const MOBILE_TOP_BAR_HEIGHT = happyHour
    ? BASE_MOBILE_TOP_BAR_HEIGHT + HAPPY_HOUR_BANNER_HEIGHT
    : BASE_MOBILE_TOP_BAR_HEIGHT
  const router = useRouter()
  const param = router.query
  const getInitialCatsFromUrl = () => {
    let res: string[] = []
    const ids = `${param.category || ''}`.split(',').filter((a: string) => a) as string[]
    ids.forEach((id) => {
      categories?.forEach((a) => {
        if (`${a.id}` === id) {
          res.push(a.link)
        }
        res = res.concat((a.subcategories || []).filter((b) => `${b.id}` === id).map((c) => c.link))
      })
    })
    return res
  }

  const getInitialRegsFromUrl = () => {
    let res: string[] = []
    const ids = `${param.region || ''}`.split(',').filter((a: string) => a) as string[]
    ids.forEach((id) => {
      regions?.forEach((a) => {
        if (`${a.id}` === id) {
          res.push(a.link)
        }
        res = res.concat((a.regions || []).filter((b) => `${b.id}` === id).map((c) => c.link))
      })
    })
    return res
  }

  const [originalResults, setOriginalResults] = useState<PlpProduct[]>(products)
  const [results, setResults] = useState<PlpProduct[]>(products)
  const initialFilters = FILTER_DROPDOWNS.map((filter) => ({ ...filter }))
  const [filterDropdowns, setFilterDropdowns] = useState<FilterDropdown[]>(initialFilters)
  const [hotDealsEnabled, setHotDealsEnabled] = useState(false)
  const [availableInclusions, setAvailableInclusions] = useState<SearchFilterDecorator[]>([])
  const [selectedInclusions, setSelectedInclusions] = useState<SearchFilterDecorator[]>(
    ((param.additional || '') as string).split(',').filter((a) => a) as SearchFilterDecorator[]
  )
  const [selectedSort, setSortedValue] = useState((param.sort || 'popular') as string)
  const [pricingValues, setPricingValues] = useState([0, 0])
  const [priceRange, setPriceRange] = useState([
    param.price_min ? +param.price_min : 0,
    param.price_max ? +param.price_max : 9999,
  ])
  const [toggledCatGroups, setToggledCatGroups] = useState<number[]>([])
  const [selectedCatFilters, setSelectedCatFilters] = useState<string[]>(getInitialCatsFromUrl())
  const [toggledRegionGroups, setToggledRegionGroups] = useState<number[]>([])
  const [selectedRegionFilters, setSelectedRegionFilters] = useState<string[]>(getInitialRegsFromUrl())
  const [displayLimit, setDisplayLimit] = useState(pageSize)
  const [visibleTotalCount, setVisibleTotalCount] = useState(products.length)

  const [productContainerHeight, setProductContainerHeight] = useState(0)
  const [toggleProductListViewed, setToggleProductListViewed] = useState(false)

  const productsRef = useRef<HTMLInputElement>(null)
  const stickyFilterBarRef = useRef<HTMLDivElement | null>(null)
  const isSticky = useStickyFilters(stickyFilterBarRef.current, productsRef.current)

  const isMobileView = useIsMobile()

  useEffect(() => {
    setOriginalResults(products)
    let values: number[] = []
    let inclusions: SearchFilterDecorator[] = []
    products?.map((product) => {
      values.push(product.fromPrice ?? 0)
      if (!product.decorators) {
        return
      }
      const productDecorators = product.decorators.map(({ name }) => name as SearchFilterDecorator)
      inclusions = [...inclusions, ...productDecorators]
    })
    setPricingValues(values!)
    setAvailableInclusions([...new Set(inclusions!)])
  }, [products])

  useEffect(() => {
    handleFilter()
    handleUrlChange()
  }, [
    priceRange,
    selectedInclusions,
    selectedSort,
    hotDealsEnabled,
    selectedRegionFilters,
    selectedCatFilters,
    displayLimit,
  ])

  const handleUrlChange = () => {
    if (changeUrl) {
      const obj: any = {}
      if (priceRange[0] !== 0 || priceRange[1] !== 9999) {
        obj.price_min = priceRange[0]
        obj.price_max = priceRange[1]
      } else {
        obj.price_min = undefined
        obj.price_max = undefined
      }
      if (selectedCatFilters && selectedCatFilters.length > 0) {
        let catIds: number[] = []
        categories?.forEach((c) => {
          if (selectedCatFilters.includes(c.link)) {
            catIds.push(c.id)
          }
          catIds = catIds.concat(
            (c.subcategories || []).filter((a) => selectedCatFilters.includes(a.link)).map((a) => a.id)
          )
        })
        obj.category = catIds.join(',')
        obj.category = obj.category || undefined
      } else {
        obj.category = undefined
      }

      if (selectedRegionFilters && selectedRegionFilters.length > 0) {
        let regIds: number[] = []
        regions?.forEach((r) => {
          regIds = regIds.concat(
            r.regions?.filter((a) => selectedRegionFilters.includes(a.link)).map((a) => a.id) || []
          )
        })
        obj.region = regIds.join(',')
        obj.region = obj.region || undefined
      } else {
        obj.region = undefined
      }

      if (hotDealsEnabled) {
        obj.special = 'on-sale'
      } else {
        obj.special = undefined
      }

      if (selectedInclusions && selectedInclusions.length > 0) {
        obj.additional = selectedInclusions.join(',')
      } else {
        obj.additional = undefined
      }

      if (selectedSort && selectedSort !== 'popular') {
        obj.sort = selectedSort
      } else {
        obj.sort = undefined
      }
      const query = {
        ...router.query,
        ...obj,
        sort: obj.sort,
      }
      delete query.lang
      delete query.part1
      const querystr = Object.keys(query)
        .filter((a) => query[a] !== undefined)
        .sort((a, b) => (a === 'sort' ? 1 : -1))
        .map((key) => `${key}=${query[key]}`)
        .join('&')
      router.push(`${window.location.pathname}${querystr ? `?${querystr}` : ''}`, undefined, { shallow: true })
    }
  }

  useEffect(() => {
    if (productsRef.current !== null) {
      setProductContainerHeight(productsRef.current.clientHeight)
    }
  }, [results, filterDropdowns])

  const viewMoreProducts = () => {
    setDisplayLimit((prevLimit) => {
      const res = prevLimit + pageSize
      if (res > 40) {
        pageSize = 16
      }
      return res
    })
  }

  const handleFilter = () => {
    let filteredProducts = originalResults

    let newFilteredProducts = sortProducts(selectedSort, filteredProducts)
    if (hotDealsEnabled) {
      newFilteredProducts = newFilteredProducts?.filter(
        (product: { hotDeals: string | any[] }) => product.hotDeals && product.hotDeals.length > 0
      )
    }

    newFilteredProducts = newFilteredProducts?.filter(
      (product: { fromPrice: number }) => product.fromPrice >= priceRange[0] && product.fromPrice <= priceRange[1]
    )

    if (selectedInclusions.length > 0) {
      newFilteredProducts = newFilteredProducts?.filter((product: { decorators: { name: string }[] }) =>
        product.decorators?.some((decorator: { name: string }) =>
          selectedInclusions.includes(decorator.name as SearchFilterDecorator)
        )
      )
    }

    if (selectedRegionFilters.length > 0) {
      newFilteredProducts = newFilteredProducts?.filter((product) => {
        return selectedRegionFilters.includes(product.canonicalRegionUrlSegment)
      })
    }
    if (selectedCatFilters.length > 0) {
      newFilteredProducts = newFilteredProducts?.filter((product) => {
        return (
          product.categoryUrlSegments?.some((segment) => selectedCatFilters.includes(segment)) ||
          product.subcategoryUrlSegments?.some((segment) => selectedCatFilters.includes(segment))
        )
      })
    }

    setVisibleTotalCount((newFilteredProducts || []).length)
    setResults((newFilteredProducts || []).slice(0, displayLimit))
    setToggleProductListViewed(true)
  }

  const roundToNearestTen = (price: number) => {
    return roundDecimalNumber(price, 1)
  }

  const handleSort = (sortBy: string) => {
    setSortedValue(sortBy)
    let result = sortProducts(sortBy, results, originalResults)
    setResults(result!)
  }

  const handleReset = () => {
    setSelectedInclusions([])
    setHotDealsEnabled(false)
    setPriceRange([0, 9999])
    setSelectedCatFilters([])
    setSelectedRegionFilters([])
    setResults(sortProducts(selectedSort, originalResults)!)
    setDisplayLimit(pageSize)
    setToggledCatGroups([])
    setToggledRegionGroups([])
    document.body.style.overflow = 'unset'
  }

  const handleOpenFilter = (filterIndex: number) => {
    toggleFilterDropdown(filterIndex)
    document.body.style.overflow = window.innerWidth < 768 ? 'hidden' : 'unset'
  }

  const handleCloseFilter = (filterIndex: number) => {
    toggleFilterDropdown(filterIndex)
    document.body.style.overflow = 'unset'
  }

  const toggleFilterDropdown = (menuIndex: number) => {
    const menuIsOpen = filterDropdowns[menuIndex].isOpen
    if (!menuIsOpen) {
      filterDropdowns.forEach((filter) => (filter.isOpen = false))
    }
    filterDropdowns[menuIndex].isOpen = !menuIsOpen
    setFilterDropdowns([...filterDropdowns])
  }

  const hasData = (name: string) => {
    switch (name) {
      case 'categories':
        return categories && categories.length > 0
      case 'regions':
        return regions && regions.length > 0
      default:
        return true
    }
  }

  const renderIndicator = (name: string) => {
    if (name === 'categories') {
      let categoriesLength = selectedCatFilters.length
      return (
        categoriesLength > 0 && <FilterIndicator>{categoriesLength <= 9 ? categoriesLength : '9+'}</FilterIndicator>
      )
    } else if (name === 'price') {
      let rangeLabel = PRICING_FILTERS.find((range) => range.min === priceRange[0] && range.max === priceRange[1])
      return <p>: {rangeLabel!.label}</p>
    } else if (name === 'regions') {
      let regionsLength = selectedRegionFilters.length
      return regionsLength > 0 && <FilterIndicator>{regionsLength <= 9 ? regionsLength : '9+'}</FilterIndicator>
    }
  }

  const renderFilterData = (name: string) => {
    switch (name) {
      case 'categories':
        return (
          categories &&
          renderCategoryFilters(
            categories,
            toggledCatGroups,
            setToggledCatGroups,
            selectedCatFilters,
            setSelectedCatFilters
          )
        )
      case 'regions':
        return (
          regions &&
          renderRegionFilters(
            regions,
            toggledRegionGroups,
            setToggledRegionGroups,
            selectedRegionFilters,
            setSelectedRegionFilters
          )
        )
      case 'price':
        return renderPriceFilters(roundToNearestTen(Math.max(...pricingValues)), priceRange, setPriceRange)
      default:
        return
    }
  }

  const renderFilterButtons = () => {
    return (
      <>
        {filterDropdowns.map(({ name, isOpen, enabledPlps }, index) => {
          const panelRef = useRef<HTMLDivElement | null>(null)
          const [mounted, setMounted] = useState(false)
          const { handleTouchStart, handleTouchEnd } = useSwipeClose(() => handleCloseFilter(index))

          // Set `mounted` to `true` after the component has mounted on the client side
          // This prevents SSR mismatches by ensuring portals are only rendered on the client
          useEffect(() => {
            setMounted(true)
          }, [])

          // Delay rendering mobile filter panel inside portal for proper animation
          useEffect(() => {
            if (mounted && isMobileView && isOpen) {
              requestAnimationFrame(() => {
                if (panelRef.current) {
                  panelRef.current.style.top // Force reflow to ensure smooth animation
                }
              })
            }
          }, [isOpen, isMobileView, mounted])

          const filterDropDownContent = (
            <>
              <FilterBg active={isOpen} className="filter-wrap" />
              <FilterDropdownPanel className="filter-wrap" active={isOpen}>
                <MobileFilterHeader
                  isAllFilters={false}
                  testIdSuffix={name}
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                >
                  <Heading size="L">{filterHeading(entityType, name)}</Heading>
                  <Close onClick={() => handleCloseFilter(index)} data-testid={`filter-close-${name}`}>
                    <Icon name="close" />
                  </Close>
                </MobileFilterHeader>
                <PanelContent data-testid={`filter-data-${name}`}>{renderFilterData(name)}</PanelContent>
                <Footer>
                  <FooterButtons>
                    <ClearButton onClick={handleReset}>Clear all</ClearButton>
                    <Button onClick={() => handleCloseFilter(index)}>Apply</Button>
                  </FooterButtons>
                </Footer>
              </FilterDropdownPanel>
            </>
          )
          return (
            <React.Fragment key={name}>
              {name === 'all filters' ? (
                <FilterDropdownButton
                  size="S"
                  inverse={!isOpen}
                  alternate={isOpen}
                  onClick={() => handleOpenFilter(index)}
                  className="all-filters filter-wrap"
                  filterOpen={isOpen}
                  dataTestId="filter-btn-all"
                  hideInDesktop={alwaysShowAllFilterSideBar}
                >
                  <Icon name="filter" />
                  <span>All filters</span>
                </FilterDropdownButton>
              ) : (
                (enabledPlps.includes('ALL') || enabledPlps.includes(entityType)) &&
                hasData(name) && (
                  <FilterDropdownWrap hideInDesktop={alwaysShowAllFilterSideBar}>
                    <FilterDropdownButton
                      size="S"
                      inverse={!isOpen}
                      alternate={isOpen}
                      filterOpen={isOpen}
                      className="filter-wrap"
                      onClick={() => handleOpenFilter(index)}
                      dataTestId={`filter-btn-${name}`}
                    >
                      <FilterDropdownButtonLabel>
                        {filterHeading(entityType, name)}
                        <span>{renderIndicator(name)}</span>
                      </FilterDropdownButtonLabel>
                    </FilterDropdownButton>
                    {/* Use portal for mobile, normal render for desktop */}
                    {mounted && isMobileView
                      ? createPortal(filterDropDownContent, document.body)
                      : isOpen && filterDropDownContent}
                  </FilterDropdownWrap>
                )
              )}
            </React.Fragment>
          )
        })}
      </>
    )
  }

  const renderResults = () => {
    const filtered = results
    if (filtered?.length === 0 || !filtered) {
      return (
        <NoResultsWrapper>
          <Heading size="S" spacing={false} bold={false}>
            No results found.
          </Heading>
        </NoResultsWrapper>
      )
    }

    return (
      <>
        <ListingPreview
          products={results}
          speedBumpTile={true}
          isRecommended={false}
          handlerKey={handlerKey}
          smallMobileTiles
          toggleProductListViewed={toggleProductListViewed}
        />
        {results.length < visibleTotalCount && (
          <>
            <Spacing size="XL" />
            <ViewMoreWrapper>
              <ViewMore onClick={viewMoreProducts}>Load more</ViewMore>
            </ViewMoreWrapper>
          </>
        )}
      </>
    )
  }

  const displayFloatingFilter = useRef(false)
  const [floatingFilterActive, setFloatingFilterActive] = useState(false)

  useEffect(() => {
    let top = document.getElementById('triggerBar')
    const topPos = top!.getBoundingClientRect().top + window.scrollY

    const handleFloatingFilterScroll = () => {
      const displaying = displayFloatingFilter.current
      if ((window.scrollY < topPos && !displaying) || (window.scrollY > topPos && displaying)) {
        return
      } else if (window.scrollY > topPos && !displaying) {
        displayFloatingFilter.current = true
        setFloatingFilterActive(true)
      } else {
        displayFloatingFilter.current = false
        setFloatingFilterActive(false)
      }
    }

    if (!window || (window && window.innerWidth > 768)) {
      return
    }
    window.addEventListener('scroll', handleFloatingFilterScroll)
    return () => {
      window.removeEventListener('scroll', handleFloatingFilterScroll)
    }
  }, [])

  useEffect(() => {
    if (alwaysShowAllFilterSideBar && window.innerWidth > BREAKPOINT_SMALL) {
      setFilterDropdowns(
        filterDropdowns.map((filter) => {
          return {
            ...filter,
            isOpen: filter.name === 'all filters',
          }
        })
      )
      return
    }
    const handleClick = (e: MouseEvent) => {
      const ele = e.target as HTMLElement

      if ((`${ele.className}` || '').indexOf('filter-wrap') < 0 && !ele.closest('.filter-wrap')) {
        setFilterDropdowns(
          filterDropdowns.map((filter) => ({
            ...filter,
            isOpen: false,
          }))
        )
      }
    }

    document.body.addEventListener('click', handleClick)

    return () => {
      document.body.removeEventListener('click', handleClick)
    }
  }, []) //

  return (
    <>
      {title && (
        <>
          <ProductsHeadingWrapper id="productsHeadingWrapper">
            <Heading as="h2" size="XL" spacing={false}>
              {title}
            </Heading>
          </ProductsHeadingWrapper>
        </>
      )}
      <StickyFiltersWrapper
        id="stickyFiltersWrap"
        isSticky={isSticky}
        topBarHeight={MOBILE_TOP_BAR_HEIGHT}
        ref={stickyFilterBarRef}
      >
        <FilterTriggerBar id="triggerBar" className="sticky-top">
          {renderFilterButtons()}
          {showTotal && <span className="total-count">{visibleTotalCount || 0} experiences found</span>}
          <Select
            name="pickup"
            ariaLabel="Sort"
            options={sortOptions.map((a) => ({ label: a.label, value: a.name }))}
            onChange={handleSort}
            value={selectedSort}
            isDisabled={!products?.length}
          />
        </FilterTriggerBar>
        <ResultsCountWrapper>
          {products.length && showTotal && (
            <p>
              <span className="total-count">
                {visibleTotalCount || 0} experiences <span className="showing-text">found</span>
              </span>
            </p>
          )}
          <div className="sort-icon">
            <Icon name="sort" />
            <span className="sort-label">{sortOptions.find((a) => a.name === selectedSort)?.label}</span>
            <Select
              name="pickup"
              options={sortOptions.map((a) => ({ label: a.label, value: a.name }))}
              onChange={handleSort}
              value={selectedSort}
              isDisabled={!products?.length}
            />
          </div>
        </ResultsCountWrapper>
      </StickyFiltersWrapper>
      <ListingWrap
        active={filterDropdowns[0].isOpen}
        activeMinHeight={productContainerHeight}
        topBarHeight={MOBILE_TOP_BAR_HEIGHT}
      >
        <FilterBg active={filterDropdowns[0].isOpen} />
        <GridLeft topBarHeight={MOBILE_TOP_BAR_HEIGHT}>
          <ListingFilters
            onClose={() => handleCloseFilter(0)}
            originalResults={originalResults}
            setProducts={setResults}
            handleFilter={handleFilter}
            reset={handleReset}
            availableInclusions={availableInclusions}
            selectedInclusions={selectedInclusions}
            hotDealsEnabled={hotDealsEnabled}
            setHotDealsEnabled={setHotDealsEnabled}
            setSelectedInclusions={setSelectedInclusions}
            onSortProp={selectedSort}
            pricingMax={pricingValues ? roundToNearestTen(Math.max(...pricingValues)) : 0}
            priceRange={priceRange}
            setPriceRange={setPriceRange}
            regions={regions}
            categories={categories}
            toggledCatGroups={toggledCatGroups}
            setToggledCatGroups={setToggledCatGroups}
            selectedCatFilters={selectedCatFilters}
            setSelectedCatFilters={setSelectedCatFilters}
            toggledRegionGroups={toggledRegionGroups}
            setToggledRegionGroups={setToggledRegionGroups}
            selectedRegionFilters={selectedRegionFilters}
            setSelectedRegionFilters={setSelectedRegionFilters}
            entityType={entityType}
          />
        </GridLeft>
        <GridRight id="productsGrid" ref={productsRef}>
          {renderResults()}
        </GridRight>
      </ListingWrap>
    </>
  )
}

export default FilterListingPreview
